import update from "immutability-helper"
import { getObjectId } from "./utils"
import { locateActionIn } from "./actionItemTreeState"

/**
 * Socket messages for activities, plan items and project fields, applied in one pass each.
 * Like applyActionItemSocketChanges (utils/actionItemTreeState.js): pure, they return the
 * new state pieces and one missionChange entry per applied change, shaped like the notice
 * the App's single helpers publish, so the App commits a whole message at once.
 *
 * Each takes the changes other tabs made; the App's socket handler filters out its own.
 */

const findMissionIndex = (missions, missionId) => missions.findIndex((m) => m._id === missionId)

/**
 * Activity notices without their task id lists. Moving a task (API PUT
 * /actionItem/:missionId/move) also sends both activities with actionItems as plain ids,
 * the "from" one with the task as its doc. Merged in, those ids replaced the task objects
 * held, and the Gantt froze on tasks with no dates. The task itself moves through its own
 * notice, so the lists carry nothing this tab needs. A notice left with no change is dropped.
 */
export const withoutTaskIdLists = (changes) => {
    const strip = (o) => {
        if (!o || typeof o !== "object" || !("actionItems" in o)) return o
        const { actionItems, ...rest } = o
        return rest
    }

    return (changes || [])
        .map((c) => (c ? { ...c, doc: strip(c.doc), change: strip(c.change) } : c))
        .filter((c) => c && c.change && Object.keys(c.change).length)
}

/**
 * Activities.
 *  create: the doc goes into its phase. An activity already there, or a project or phase
 *    this tab doesn't hold, is skipped (the single helper reloaded the page for an unknown
 *    project, and pushed into index -1 for an unknown phase).
 *  update: a full doc (with actionItems) replaces the activity, its task ids filled in from
 *    the tasks held. A short doc (the Gantt's bulk endpoint: missionId + changed fields) is
 *    merged by id, and each project's short changes publish as one "bulk" entry, after the
 *    full ones, as the handler always did.
 *  delete: removes the activity (the single helper crashed when it wasn't found).
 *
 * @param items {{ _id?: string, doc: object, change?: object }[]}
 */
export const applyActionSocketChanges = (state, kind, items) => {
    let missions = state.missions
    const entries = []
    const shortByMission = new Map()

    ;(items || []).forEach((item) => {
        const doc = item?.doc

        if (!doc) return

        const missionId = getObjectId(doc.missionId)
        const missionIndex = findMissionIndex(missions, missionId)

        if (kind === "create") {
            if (missionIndex === -1 || !doc._id || locateActionIn(missions, missionId, doc._id)) return

            const phaseIndex = (missions[missionIndex].planItems || []).findIndex(
                (p) => p._id === getObjectId(doc.missionPlanId),
            )

            if (phaseIndex === -1) return

            missions = update(missions, {
                [missionIndex]: { planItems: { [phaseIndex]: { actions: { $push: [doc] } } } },
            })

            entries.push({ for: "action", change: "new", obj: doc, missionId })
        } else if (kind === "update") {
            if (!doc.actionItems) {
                // Short doc: only for projects this tab has loaded in full
                const id = item._id || doc._id

                if (!id || missionIndex === -1 || !missions[missionIndex].planItems) return

                if (!shortByMission.has(missionId)) shortByMission.set(missionId, [])
                shortByMission.get(missionId).push({ ...item.change, _id: id })
                return
            }

            const change = item.change
            const at = missionIndex === -1 ? false : locateActionIn(missions, missionId, doc._id)

            if (!at) {
                // Not held: the single helper still published, so screens could refetch
                entries.push({ for: "action", change, obj: doc, missionId })
                return
            }

            const current = missions[at.missionIndex].planItems[at.missionPhaseIndex].actions[at.missionActionIndex]
            const heldTasks = current.actionItems || []
            const actionItems = doc.actionItems
                .map((ai) => (typeof ai === "string" ? heldTasks.find((t) => t._id === ai) : ai))
                .filter(Boolean)
            const obj = { ...doc, actionItems }

            missions = update(missions, {
                [at.missionIndex]: {
                    planItems: {
                        [at.missionPhaseIndex]: {
                            actions: { [at.missionActionIndex]: { $set: { ...obj, ...change, actionItems } } },
                        },
                    },
                },
            })

            entries.push({ for: "action", change, obj, missionId })
        } else if (kind === "delete") {
            let at = missionIndex === -1 || !doc._id ? false : locateActionIn(missions, missionId, doc._id)

            if (!at) return

            // A deleted board section hands its tasks to the action the API moved them to
            const moveTo = doc.movedTo && locateActionIn(missions, missionId, getObjectId(doc.movedTo._id))

            if (moveTo) {
                const moved = (
                    missions[at.missionIndex].planItems[at.missionPhaseIndex].actions[at.missionActionIndex]
                        .actionItems || []
                ).map((ai) => ({
                    ...ai,
                    actionId: getObjectId(doc.movedTo._id),
                    missionPlanId: getObjectId(doc.movedTo.missionPlanId),
                }))

                missions = update(missions, {
                    [moveTo.missionIndex]: {
                        planItems: {
                            [moveTo.missionPhaseIndex]: {
                                actions: {
                                    [moveTo.missionActionIndex]: {
                                        actionItems: { $apply: (list) => [...(list || []), ...moved] },
                                    },
                                },
                            },
                        },
                    },
                })
                at = locateActionIn(missions, missionId, doc._id)
            }

            missions = update(missions, {
                [at.missionIndex]: {
                    planItems: { [at.missionPhaseIndex]: { actions: { $splice: [[at.missionActionIndex, 1]] } } },
                },
            })

            entries.push({ for: "action", change: "delete", obj: doc, missionId })
        }
    })

    shortByMission.forEach((changes, missionId) => {
        const missionIndex = findMissionIndex(missions, missionId)
        const byId = new Map(changes.map((c) => [c._id, c]))

        missions = update(missions, {
            [missionIndex]: {
                planItems: {
                    $apply: (planItems) =>
                        planItems.map((planItem) => {
                            if (!(planItem.actions || []).some((a) => byId.has(a._id))) return planItem

                            return {
                                ...planItem,
                                actions: planItem.actions.map((a) => (byId.has(a._id) ? { ...a, ...byId.get(a._id) } : a)),
                            }
                        }),
                },
            },
        })

        entries.push({ for: "action", change: "bulk", changes, missionId })
    })

    return { missions, entries }
}

/**
 * Plan items (phases, roles, costs).
 *  create: pushed onto the project; one already there is skipped.
 *  update: merged, and into the open phase panel (phaseObj) when it's that one. As the
 *    single helper, a project without an org publishes no entry for it.
 *  delete: removed.
 * orgMissionIds: projects with an org that changed, so the App can tell the org screens.
 *
 * @param state {{ missions, phaseObj?, orgs? }}
 * @param items {{ doc: object, change?: object }[]}
 */
export const applyPlanItemSocketChanges = (state, kind, items) => {
    let missions = state.missions
    let phaseObj = state.phaseObj
    const entries = []
    const orgMissionIds = new Set()

    ;(items || []).forEach((item) => {
        const doc = item?.doc

        if (!doc?._id) return

        const missionId = getObjectId(doc.missionId)
        const missionIndex = findMissionIndex(missions, missionId)

        if (missionIndex === -1) return

        const mission = missions[missionIndex]
        const planItemIndex = (mission.planItems || []).findIndex((p) => p._id === doc._id)
        const orgId = mission.org ? getObjectId(mission.org) : null

        if (kind === "create") {
            if (planItemIndex !== -1) return

            missions = update(missions, { [missionIndex]: { planItems: { $push: [doc] } } })
            entries.push({ for: "planItem", change: "create", obj: doc, missionId })
        } else if (kind === "update") {
            const change = item.change

            if (!change || planItemIndex === -1) return

            missions = update(missions, { [missionIndex]: { planItems: { [planItemIndex]: { $merge: change } } } })

            if (phaseObj && phaseObj._id === doc._id) {
                phaseObj = { ...phaseObj, ...change }
            }

            if (orgId) {
                entries.push({
                    for: "planItem",
                    obj: doc,
                    change,
                    missionId,
                    orgId,
                    newStateOrg: (state.orgs || []).find((o) => o._id === orgId),
                })
            }
        } else if (kind === "delete") {
            if (planItemIndex === -1) return

            missions = update(missions, { [missionIndex]: { planItems: { $splice: [[planItemIndex, 1]] } } })
            entries.push({ for: "planItem", change: "delete", obj: doc, missionId })
        } else {
            return
        }

        if (orgId) orgMissionIds.add(missionId)
    })

    return { missions, phaseObj, entries, orgMissionIds: [...orgMissionIds] }
}

/**
 * Plain project field changes (not people, not archive): merged into each project, one
 * entry each, as missionUpdateState publishes them (utils/mission.js).
 *
 * @param items {{ _id: string, change: object }[]}
 */
export const applyMissionSocketUpdates = (state, items) => {
    let missions = state.missions
    const entries = []

    ;(items || []).forEach((item) => {
        const missionIndex = findMissionIndex(missions, item?._id)

        if (missionIndex === -1 || !item.change) return

        missions = update(missions, { [missionIndex]: { $merge: item.change } })

        entries.push({
            for: "mission",
            change: item.change,
            obj: missions[missionIndex],
            missionId: item._id,
        })
    })

    return { missions, entries }
}
